import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AssetPreview,
  Badge,
  Button,
  Card,
  Cluster,
  InfinityMark,
  Stack,
  Stat,
} from '@integra/ui';

/**
 * Public product tour (§3.3, §20.1). A LIVE in-app demo — every scene is composed
 * from the real @integra/ui components on the real design tokens, with sample data,
 * so a visitor sees the actual platform surfaces (not a screenshot or a mock).
 *
 * The tour auto-advances through the three jobs INTEGRA does — Activate, Govern,
 * Optimise — each a self-contained tableau of real components. Client-agnostic
 * (§4.1); no data access (§21.4) — the sample content is local and illustrative.
 */

const CHAPTERS = [
  {
    key: 'activate',
    idx: '01',
    name: 'Activate',
    kicker: 'Distribute',
    path: 'app.integra.fruitfulbough.uk / assets',
    caption: 'One asset, adapted per platform and sent to every channel — native on each.',
  },
  {
    key: 'govern',
    idx: '02',
    name: 'Govern',
    kicker: 'Assure',
    path: 'app.integra.fruitfulbough.uk / campaigns',
    caption: 'Nothing ships without sign-off — and every change lands on the audit trail.',
  },
  {
    key: 'optimise',
    idx: '03',
    name: 'Optimise',
    kicker: 'Intelligence',
    path: 'app.integra.fruitfulbough.uk / performance',
    caption: 'Measure organic and paid as one; the evidence promotes the winner.',
  },
] as const;

const CHANNELS = ['LinkedIn', 'Instagram', 'Facebook', 'Pinterest', 'X', 'Email'];

/** Scene 1 — the real Assets grid + a one-action fan-out to every channel. */
function ActivateScene() {
  return (
    <Stack gap="lg">
      <div className="grid gap-4 sm:grid-cols-3">
        <AssetPreview
          title="Autumn collection launch"
          body="The hero story for the season — one source, every channel."
          type="article"
          state="active"
        />
        <AssetPreview
          title="Harvest gift box"
          body="Signature product feature, adapted for shopfront and social."
          type="product"
          state="active"
        />
        <AssetPreview
          title="Winter lookbook"
          body="Editorial set, ready to sequence across the quarter."
          type="image"
          state="draft"
        />
      </div>
      <Card>
        <Cluster justify="between" align="center">
          <Stack gap="sm">
            <p className="font-heading text-base text-navy-700">
              Publish to every connected channel
            </p>
            <Cluster gap="sm">
              {CHANNELS.map((c) => (
                <Badge key={c} tone="neutral">
                  {c}
                </Badge>
              ))}
            </Cluster>
          </Stack>
          <Button>Publish once →</Button>
        </Cluster>
      </Card>
    </Stack>
  );
}

/** Scene 2 — the governed action: approval gate → release → append-only audit. */
function GovernScene() {
  return (
    <Stack gap="lg">
      <Card title="Autumn launch · 6 channels" subtitle="Rendered and staged — waiting on approval">
        <Cluster justify="between" align="center">
          <Badge tone="warning">Awaiting approval</Badge>
          <Button>Approve &amp; release</Button>
        </Cluster>
      </Card>
      <Alert tone="success" title="Approved &amp; released">
        Approved by A. Mensah · logged to the append-only audit with the before-and-after.
      </Alert>
      <Card title="Brand safety" subtitle="Checked at publish time, per workspace">
        <Cluster gap="sm">
          <Badge tone="success">0 prohibited terms</Badge>
          <Badge tone="neutral">2 review terms cleared</Badge>
          <Badge tone="neutral">Specs in sync</Badge>
        </Cluster>
      </Card>
    </Stack>
  );
}

/** Scene 3 — measurement with provenance, and an experiment that picks the winner. */
function OptimiseScene() {
  return (
    <Stack gap="lg">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Reach (7 channels)" value="128,400" provenance="measured" />
        <Stat
          label="Engagement rate"
          value="4.8%"
          provenance="measured"
          hint="Organic + paid, as one"
        />
        <Stat label="Attributed revenue" value="£42,180" provenance="estimated" />
      </div>
      <Card title="A/B experiment · hero copy" subtitle="Winner promoted from measured data">
        <Stack gap="sm">
          <Cluster justify="between" align="center">
            <span className="text-sm text-ink-muted">Variant A</span>
            <span className="text-sm text-ink-muted">3.1% engagement</span>
          </Cluster>
          <Cluster justify="between" align="center">
            <span className="text-sm font-semibold text-navy-700">Variant B</span>
            <Badge tone="success">Winner · +38%</Badge>
          </Cluster>
        </Stack>
      </Card>
    </Stack>
  );
}

const SCENES = {
  activate: <ActivateScene />,
  govern: <GovernScene />,
  optimise: <OptimiseScene />,
} as const;

const CHAPTER_MS = 7000;

export function DemoPage() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const startedAt = useRef<number>(0);

  // Auto-advance: a lightweight timer drives the progress bar and rolls to the
  // next chapter. Re-runs whenever the chapter or play-state changes (§21.2 note:
  // this is a UI presentation timer, not backend scheduling).
  useEffect(() => {
    if (!playing) return;
    startedAt.current = Date.now();
    const tick = globalThis.setInterval(() => {
      const p = Math.min(1, (Date.now() - startedAt.current) / CHAPTER_MS);
      setProgress(p);
      if (p >= 1) setIdx((i) => (i + 1) % CHAPTERS.length);
    }, 60);
    return () => globalThis.clearInterval(tick);
  }, [playing, idx]);

  const active = CHAPTERS[idx] ?? CHAPTERS[0];
  const goTo = (i: number) => {
    setIdx(i);
    setProgress(0);
  };
  const scene = useMemo(() => SCENES[active.key], [active.key]);

  return (
    <div className="min-h-screen bg-surface">
      {/* Utility strip */}
      <div className="bg-navy-800 text-navy-300">
        <div className="mx-auto flex h-9 max-w-6xl items-center justify-between px-6">
          <span className="font-mono text-[11px] uppercase tracking-[0.14em]">
            A Fruitful Bough platform
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-gold-400">
            Live product tour
          </span>
        </div>
      </div>

      {/* Header */}
      <header className="bg-navy-800">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            aria-label="INTEGRA home"
            className="flex items-center gap-2.5 rounded-md transition-opacity duration-fast hover:opacity-80"
          >
            <InfinityMark size={26} className="text-gold-500" />
            <span className="font-heading text-lg text-ink-inverse">INTEGRA</span>
          </button>
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-sm text-navy-300 transition-colors duration-fast hover:text-ink-inverse"
          >
            ← Back to site
          </button>
        </div>
      </header>

      {/* Stage */}
      <section className="bg-navy-800 pb-16 pt-8 text-ink-inverse">
        <div className="mx-auto max-w-6xl px-6">
          <p className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-navy-300">
            The 90-second tour
          </p>
          <h1 className="mt-3 max-w-2xl font-heading text-3xl leading-tight sm:text-4xl">
            See INTEGRA work — the real platform, one governed run.
          </h1>

          <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_300px]">
            {/* Browser frame with the live scene */}
            <div className="overflow-hidden rounded-xl border border-navy-500 bg-surface shadow-raised">
              <div className="flex h-10 items-center gap-2 border-b border-edge bg-surface-sunken px-4">
                <span className="h-2.5 w-2.5 rounded-full bg-border-strong" />
                <span className="h-2.5 w-2.5 rounded-full bg-border-strong" />
                <span className="h-2.5 w-2.5 rounded-full bg-border-strong" />
                <span className="ml-3 truncate font-mono text-[11px] text-ink-muted">
                  {active.path}
                </span>
              </div>
              <div className="min-h-[360px] p-6">
                <div className="mb-5">
                  <p className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-gold-text">
                    {active.idx} — {active.name}
                  </p>
                </div>
                {scene}
              </div>
              <div className="border-t border-edge bg-surface-muted px-6 py-3">
                <p className="text-sm text-ink-muted">
                  <span className="font-mono text-xs text-gold-text">
                    {active.idx} / {String(CHAPTERS.length).padStart(2, '0')}
                  </span>{' '}
                  {active.caption}
                </p>
              </div>
            </div>

            {/* Chapter rail */}
            <div className="flex flex-col gap-3">
              {CHAPTERS.map((c, i) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => goTo(i)}
                  className={
                    i === idx
                      ? 'rounded-md border border-gold-500 bg-navy-700 p-3.5 text-left transition-colors duration-fast'
                      : 'rounded-md border border-navy-600 p-3.5 text-left transition-colors duration-fast hover:border-navy-500'
                  }
                >
                  <span className="font-mono text-[10px] tracking-[0.14em] text-gold-400">
                    {c.idx}
                  </span>
                  <p className="mt-1 font-heading text-base text-ink-inverse">{c.name}</p>
                  <p className="mt-0.5 text-xs text-navy-300">{c.kicker}</p>
                  <div className="mt-2.5 h-[3px] overflow-hidden rounded-full bg-navy-600">
                    <div
                      className="h-full rounded-full bg-gold-500"
                      style={{
                        width: `${String(i === idx ? progress * 100 : i < idx ? 100 : 0)}%`,
                      }}
                    />
                  </div>
                </button>
              ))}
              <Cluster gap="sm" align="center">
                <button
                  type="button"
                  onClick={() => setPlaying((p) => !p)}
                  className="rounded-md border border-navy-500 px-3 py-1.5 text-xs font-semibold text-ink-inverse transition-colors duration-fast hover:border-gold-400 hover:text-gold-400"
                >
                  {playing ? '❙❙ Pause' : '▶ Play'}
                </button>
                <span className="font-mono text-[11px] text-navy-300">
                  {playing ? 'Playing · auto' : 'Paused'}
                </span>
              </Cluster>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-6 py-14 text-center">
        <h2 className="max-w-xl font-heading text-2xl text-navy-700 sm:text-3xl">
          That&rsquo;s the loop — every asset, governed end to end.
        </h2>
        <p className="max-w-lg text-ink-muted">
          Everything in this tour is the real product, live. Sign in to run it on your own channels.
        </p>
        <Cluster gap="sm">
          <Button onClick={() => navigate('/login')}>Sign in</Button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="rounded-sm border border-border-strong px-4 py-2 text-sm font-semibold text-navy-700 transition-colors duration-fast hover:border-navy-700"
          >
            See the platform
          </button>
        </Cluster>
      </section>
    </div>
  );
}
